import { Link } from 'react-router-dom';
import { User, ArrowRight, Users } from 'lucide-react';
import { DentalOfficePatient } from '@/types';

interface RecentPatientsProps {
  patients: DentalOfficePatient[];
  loading?: boolean;
}

export function RecentPatients({ patients, loading }: RecentPatientsProps) {
  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-soft p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="h-6 bg-gray-200 rounded w-32 animate-pulse"></div>
          <div className="h-5 bg-gray-200 rounded w-20 animate-pulse"></div>
        </div>
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center gap-3 animate-pulse">
              <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-24"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (patients.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-soft p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Nýlegir sjúklingar</h2>
          <Link
            to="/patients"
            className="text-sm text-brand-primary hover:text-brand-primary/80 font-medium flex items-center gap-1"
          >
            Sjá allt
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="text-center py-8">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">Engir sjúklingar skráðir</p>
          <Link
            to="/patients/new"
            className="inline-block mt-4 text-brand-primary hover:text-brand-primary/80 font-medium text-sm"
          >
            Skrá nýjan sjúkling
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-soft p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Nýlegir sjúklingar</h2>
        <Link
          to="/patients"
          className="text-sm text-brand-primary hover:text-brand-primary/80 font-medium flex items-center gap-1"
        >
          Sjá allt
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="space-y-4">
        {patients.map((patient) => (
          <Link
            key={patient.id}
            to={`/patients/${patient.id}`}
            className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors group"
          >
            <div className="w-10 h-10 bg-brand-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
              <User className="w-5 h-5 text-brand-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 group-hover:text-brand-primary transition-colors">
                {patient.first_name} {patient.last_name}
              </p>
              <p className="text-xs text-gray-600">
                {patient.last_visit_date
                  ? `Síðasta heimsókn: ${new Date(patient.last_visit_date).toLocaleDateString('is-IS')}`
                  : 'Engin heimsókn skráð'}
              </p>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-brand-primary transition-colors" />
          </Link>
        ))}
      </div>
    </div>
  );
}
